import React from 'react';
import { Link } from 'react-router-dom';

const Privacy = () => {
  const lastUpdated = 'January 15, 2024';

  const sections = [
    {
      title: '1. Information We Collect',
      content: 'We collect information you provide directly to us, including name, email address, social media account details, and payment information. We also collect usage data and analytics information to improve our services.'
    },
    {
      title: '2. How We Use Your Information',
      content: 'We use your information to: (a) provide and maintain our services; (b) process transactions; (c) communicate with you; (d) improve our services; (e) comply with legal obligations.'
    },
    {
      title: '3. Information Sharing',
      content: 'We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy. We may share information with: (a) service providers; (b) legal authorities when required; (c) business partners with your consent.'
    },
    {
      title: '4. Data Security',
      content: 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. We use SSL encryption for all data transmissions.'
    },
    {
      title: '5. Cookies and Tracking',
      content: 'We use cookies and similar technologies to enhance your experience, analyze usage, and assist in our marketing efforts. You can control cookies through your browser settings.'
    },
    {
      title: '6. Your Rights',
      content: 'You have the right to: (a) access your personal information; (b) correct inaccurate information; (c) request deletion of your information; (d) object to processing; (e) data portability; (f) withdraw consent.'
    },
    {
      title: '7. Children\'s Privacy',
      content: 'Our services are not intended for children under 13. We do not knowingly collect personal information from children under 13. If we become aware of such information, we will delete it promptly.'
    },
    {
      title: '8. International Data Transfers',
      content: 'Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.'
    },
    {
      title: '9. Changes to This Policy',
      content: 'We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.'
    }
  ];

  return (
    <div className="pt-16">
      {/* Header */}
      <section className="hero-gradient text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto">
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <p className="text-gray-600">
              Last updated: <span className="font-semibold">{lastUpdated}</span>
            </p>
          </div>

          <div className="space-y-12">
            {sections.map((section, index) => (
              <div key={index} className="border-b border-gray-200 pb-8 last:border-b-0">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {section.title}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {section.content}
                </p>
              </div>
            ))}
          </div>

          {/* Contact Information */}
          <div className="mt-16 p-8 bg-purple-50 rounded-lg">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Privacy Questions?
            </h3>
            <p className="text-gray-600 mb-6">
              If you have any questions about this Privacy Policy or how we handle your information, please contact us:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-gray-700">
                  <strong>Email:</strong> privacy@followershub.com
                </p>
                <p className="text-gray-700">
                  <strong>Phone:</strong> +1 (555) 123-4567
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-gray-700">
                  <strong>Address:</strong> 123 Business St, Suite 100
                </p>
                <p className="text-gray-700">
                  New York, NY 10001
                </p>
              </div>
            </div>
          </div>

          {/* Data Protection Rights */}
          <div className="mt-12 p-8 bg-purple-100 rounded-lg">
            <h3 className="text-xl font-bold text-purple-800 mb-4">
              Your Data Protection Rights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Access to your personal data',
                'Correction of inaccurate data',
                'Right to be forgotten',
                'Data portability',
                'Right to object processing',
                'Withdraw consent'
              ].map((right, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs font-bold">✓</span>
                  </div>
                  <span className="text-purple-800">{right}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold gradient-text mb-6">
              Commitment to Privacy
            </h3>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto mb-8">
              At Followers Hub, we are committed to protecting your privacy and ensuring the security of your personal information. We continuously review our practices to ensure they comply with applicable data protection laws.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Contact Privacy Team
              </Link>
              <Link to="/terms" className="btn-secondary">
                View Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
